import React, { useEffect, useState } from "react";
import { useCreatorCardsContext } from "../../../context/Creator/CreatorCardsProvider";
import { useNavigate, useParams } from "react-router-dom";
import Card from "../../Cards/Card";
import styles from "./AddCardsToBooster.module.css";
import { useCreatorBoostersContext } from "../../../context/Creator/CreatorBoostersProvider";
import {
  BoosterCards,
  boosterData,
  createBoosterCards,
  getCardsForBooster,
} from "../../../services/BoosterServices";
import Booster from "../Booster";
import FilterCards from "../../Cards/FilterCards/FilterCards";
import InfiniteScroll from "../../InfiniteScroll/InfiniteScroll";
// import { useAlert } from "../../../context/AlertProvider";
import { rarityEnum } from "../../../enums/rarityEnum";
import globalStyles from "../../../styles/globalStyles.module.css";
import { useAlert } from "../../../context/AlertProvider";
import { useTranslation } from "react-i18next";
import cardStyles from "../../Cards/ShowCards/ShowCards.module.css";
import CancelButton from "../../Buttons/CancelButton/CancelButton";

type RarityDistribution = {
  common: number;
  silver: number;
  gold: number;
};

const AddCardsToBooster: React.FC = () => {
  const { boosterId } = useParams<{ boosterId: string }>();
  const { cardsState } = useCreatorCardsContext();
  const navigate = useNavigate();
  const [booster, setBooster] = useState<boosterData>();
  const { boostersState } = useCreatorBoostersContext();
  const [rarityDistribution, setRarityDistribution] =
    useState<RarityDistribution>({
      common: 0,
      silver: 0,
      gold: 0,
    });
  const [selectedCards, setSelectedCards] = useState<BoosterCards[]>([]);
  const [showSelected, setShowSelected] = useState(true);
  const { showAlert } = useAlert();
  const { t } = useTranslation();

  useEffect(() => {
    const id = boosterId;
    if (id) {
      (async () => {
        try {
          const boosterToUpdate = boostersState.data.find((e) => e.id === id);
          if (!boosterToUpdate) {
            navigate(-1);
            return;
          }

          const fetchedCards = await getCardsForBooster(id);

          setSelectedCards(fetchedCards);

          const common = fetchedCards.filter(
            (fetchedCard) => fetchedCard.rarity === rarityEnum.common
          ).length;

          const silver = fetchedCards.filter(
            (fetchedCard) => fetchedCard.rarity === rarityEnum.silver
          ).length;

          const gold = fetchedCards.filter(
            (fetchedCard) => fetchedCard.rarity === rarityEnum.gold
          ).length;

          const initRarityDistribution: RarityDistribution = {
            common: common,
            silver: silver,
            gold: gold,
          };

          setRarityDistribution(initRarityDistribution);

          setBooster(boosterToUpdate);
        } catch (error) {
          console.error("Failed to fetch booster:", error);
        }
      })();
    }

    // only rerun on boosterId change ...
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boosterId]);

  const modifyRarity = (
    property: keyof RarityDistribution,
    action: "increment" | "decrement"
  ) => {
    setRarityDistribution((prevState) => ({
      ...prevState,
      [property]:
        action === "increment"
          ? prevState[property] + 1
          : prevState[property] - 1, // Decrement logic
    }));
  };

  const toggleCardSelection = (cardId: number) => {
    setSelectedCards((prevSelectedCards) => {
      const isSelected = prevSelectedCards.find((card) => card.id === cardId);

      if (isSelected) {
        modifyRarity(isSelected.rarity, "decrement");
        cardsState.setData((prevCards) => {
          return prevCards.map((card) =>
            card.id === cardId ? { ...card, isSelected } : card
          );
        });
        return prevSelectedCards.filter((card) => card.id !== cardId);
      }

      const selectedCard = cardsState.filteredData.find(
        (card) => card.id === cardId
      );
      if (!selectedCard) return prevSelectedCards;

      modifyRarity(selectedCard.rarity, "increment");

      const newCard = {
        id: selectedCard.id,
        name: selectedCard.name,
        image_id: selectedCard.image_id,
        blurred_image_id: selectedCard.blurred_image_id,
        small_image_id: selectedCard.small_image_id,
        is_owned: false,
        blur_level: selectedCard.blur_level,
        rarity: selectedCard.rarity,
        updated_at: selectedCard.updated_at,
      };

      return [...prevSelectedCards, newCard];
    });
  };
  const addToBooster = async () => {
    if (!booster?.id) {
      console.error("Booster ID is missing", booster);
      return;
    }

    try {
      await createBoosterCards(
        booster.id,
        selectedCards.map((card) => card.id)
      );
      showAlert("Booster updated");
    } catch (error) {
      showAlert("Booster is already bought and cannot be updated anymore");
    }
    navigate("/creator/boosters");
  };

  const handleShowSelected = () => {
    setShowSelected((prev) => !prev);
  };

  return (
    <>
      <div className={cardStyles.actionButtonsContainer}>
        <div className={cardStyles.actionButtons}>
          <CancelButton />
          <button className={globalStyles.button} onClick={addToBooster}>
            {t("update")}
          </button>
        </div>
      </div>
      <div className={globalStyles.pageTitle}>Booster collection</div>
      <div className={styles.headerContainer}>
        {booster && (
          <div className={styles.boosterSize}>
            <Booster
              booster={booster}
              key={booster.id}
              isInStore={false}
              disableHover={true}
            />
          </div>
        )}
        <div className={styles.boosterInfo}>
          <div>
            <h2>{t("information")}</h2>
            <h4>{t("name")}</h4>
            <div>{booster?.name}</div>
            <h4>{t("description")}</h4>
            <div>{booster?.description}</div>

            {/* <h4>{t("rarityDistribution")}</h4>
            <div>
              {t("common")} : {rarityDistribution.common}
            </div>
            <div>
              {t("silver")} : {rarityDistribution.silver}
            </div>
            <div>
              {t("gold")} : {rarityDistribution.gold}
            </div> */}
            {/* <div>
              {" "}
              {t("total")} :{" "}
              {rarityDistribution.common +
                rarityDistribution.silver +
                rarityDistribution.gold}
            </div> */}
          </div>
        </div>
      </div>

      <div className={styles.flexContainer}>
        <h4 className={styles.title}>
          {" "}
          {rarityDistribution.common +
            rarityDistribution.silver +
            rarityDistribution.gold}{" "}
          {t("cards.selected")}
        </h4>
        <button className={globalStyles.button} onClick={handleShowSelected}>
          {showSelected ? t("hide") : t("show")}
        </button>
      </div>

      {showSelected && (
        <div className={styles.selectedCardContainer}>
          {selectedCards
            .slice()
            .reverse()
            .map((card) => (
              <div
                key={card.id}
                className={`${styles.cardWidth} ${styles.selected}`}
                onClick={() => toggleCardSelection(card.id)}
              >
                <div className={styles.added}>{t("added")}</div>
                <Card cardData={card} disableClick={true} />
              </div>
            ))}
        </div>
      )}
      <h4 className={styles.title}>{t("available")}</h4>
      <FilterCards
        cards={cardsState.data}
        handleFilterCards={cardsState.setFilteredData}
      />

      <InfiniteScroll
        togglePage={cardsState.togglePage}
        loading={cardsState.loading}
        hasMore={cardsState.hasMore}
      >
        <div className={styles.cardContainer}>
          {cardsState.filteredData
            // .filter((card) => card.booster_id === null || card.booster_id === 0)
            .filter(
              (card) =>
                !selectedCards.some((e) => e.id === card.id) &&
                (card.booster_id === null || card.booster_id === booster?.id)
            )
            .map((card) => (
              <div
                key={card.id}
                className={`${styles.cardWidth} ${
                  selectedCards.find((e) => e.id === card.id)
                    ? styles.selected
                    : ""
                }`}
                onClick={() => toggleCardSelection(card.id)}
              >
                {selectedCards.find((e) => e.id === card.id) && (
                  <div className={styles.added}>{t("added")}</div>
                )}
                <Card cardData={card} disableClick={true} />
              </div>
            ))}
        </div>
      </InfiniteScroll>
    </>
  );
};

export default AddCardsToBooster;
